// Signature / initials / contractor injection helpers.
//
// Templates mark every dynamic slot with a data attribute:
//   data-sig-role="signature"       -> replace with customer signature <img>
//   data-sig-role="date"            -> replace with formatted customer date text
//   data-sig-role="name"            -> replace with signer name text
//   data-customer-initials="true"   -> replace with customer initials text
//   data-contractor-signature="true"-> replace with contractor signature <img>
//   data-contractor-name="true"     -> replace with contractor name text
//   data-contractor-date="true"     -> replace with formatted contractor date text
//
// Multiple slots per attribute are supported (e.g. Customer Initials appears on
// 9 different pages of the agreement), so every matched slot is filled.

const SIGNATURE_IMG_CLASS = 'h-16';
const SIGNATURE_IMG_MAX_HEIGHT = '40px';

// Safe wrapper around `new Date(x).toLocaleDateString()` — returns an empty
// string when the input is missing or unparseable instead of the literal
// text "Invalid Date" that leaks into the rendered document otherwise.
const safeLocaleDate = (raw) => {
  if (raw === undefined || raw === null || raw === '') return '';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

// Helper: swap the inner <div> if present (used by our signature slots to keep
// a fixed-height writing surface); fall back to the slot itself.
const targetFor = (slot) => slot.querySelector('div') || slot;

const buildSignatureImg = (dataUrl) => {
  const img = document.createElement('img');
  img.src = dataUrl;
  img.alt = 'Signature';
  img.className = SIGNATURE_IMG_CLASS;
  img.style.maxHeight = SIGNATURE_IMG_MAX_HEIGHT;
  img.style.verticalAlign = 'middle';
  return img;
};

const setImage = (root, selector, dataUrl) => {
  if (!dataUrl) return;
  root.querySelectorAll(selector).forEach((slot) => {
    const target = targetFor(slot);
    target.innerHTML = '';
    target.appendChild(buildSignatureImg(dataUrl));
  });
};

const setText = (root, selector, text) => {
  if (text === undefined || text === null || text === '') return;
  root.querySelectorAll(selector).forEach((slot) => {
    const target = targetFor(slot);
    target.textContent = text;
  });
};

// Inject a customer signature (image + date + name + initials) into every
// matching slot in `root`.
export const injectCustomerSignature = (root, {
  signatureImage,
  date,
  name,
  initials
} = {}) => {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  setImage(root, '[data-sig-role="signature"]', signatureImage);

  const dateStr = safeLocaleDate(date);
  if (dateStr) {
    setText(root, '[data-sig-role="date"]', dateStr);
  }
  setText(root, '[data-sig-role="name"]', name);
  setText(root, '[data-customer-initials]', initials);
};

// Inject contractor name, drawn signature, and date into every matching slot.
export const injectContractorSignature = (root, {
  signatureImage,
  name,
  date
} = {}) => {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  setImage(root, '[data-contractor-signature]', signatureImage);
  setText(root, '[data-contractor-name]', name);
  const dateStr = safeLocaleDate(date);
  if (dateStr) {
    setText(root, '[data-contractor-date]', dateStr);
  }
};

// Inject user-entered agreement details (Page 1 approximate dates, Page 3
// payment schedule + Agreement Costs table cells) into every matching slot.
//
// Schema:
//   agreementDetails = {
//     approxStartDate, approxCompletionDate, approxDuration,
//     progressPaymentUpon, cancellationDeadline,
//     agreementCosts: {
//       downPayment:      { amount, datePaid, checkNumber, initialed },
//       mobilization:     { ... },
//       progressPayment:  { ... },
//       balance:          { ... },
//       total:            { ... },
//       buildingPermit:   { ... },
//       permittingService:{ ... }
//     }
//   }
//
// Template markup:
//   <span data-agreement-field="approxStartDate"></span>
//   <td   data-agreement-cost="downPayment.amount"></td>
export const injectAgreementDetails = (root, agreementDetails = {}) => {
  if (!root || typeof root.querySelectorAll !== 'function' || !agreementDetails) return;

  const scalarFields = [
    'approxStartDate',
    'approxCompletionDate',
    'approxDuration',
    'progressPaymentUpon',
    'cancellationDeadline'
  ];

  scalarFields.forEach((field) => {
    const value = agreementDetails[field];
    if (value === undefined || value === null || value === '') return;
    root.querySelectorAll(`[data-agreement-field="${field}"]`).forEach((el) => {
      el.textContent = value;
    });
  });

  const costs = agreementDetails.agreementCosts || {};
  root.querySelectorAll('[data-agreement-cost]').forEach((el) => {
    const path = el.getAttribute('data-agreement-cost'); // e.g. "downPayment.amount"
    if (!path) return;
    const [row, col] = path.split('.');
    const value = costs?.[row]?.[col];
    if (value === undefined || value === null || value === '') return;
    el.textContent = value;
  });
};

// Clear the contents of every customer-signature / agreement slot (used to
// reset the DOM after print-to-PDF operations).
export const clearCustomerSignatureSlots = (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  const selectors = [
    '[data-sig-role]',
    '[data-customer-initials]',
    '[data-contractor-signature]',
    '[data-contractor-name]',
    '[data-contractor-date]'
  ];
  root.querySelectorAll(selectors.join(',')).forEach((slot) => {
    const target = targetFor(slot);
    target.innerHTML = '';
  });
};
